import React from 'react';
import { useLocation, Navigate } from "react-router-dom";

type AuthPayload = { // user data for signin
  username: string,
  fullname: string,
  mail: string | null,
  groups: string[],
  timeout: number,
  avatar?: string | null,
  appdata?: any | null
};

export interface IAuthUser extends AuthPayload {
  last: number,
}

type AuthAction = { type: "auth.user.signin"; payload: AuthPayload } | { type: "auth.user.signout" };

interface AuthContextActions {
  signIn: (data: AuthPayload) => void;
  signOut: () => void;
}

interface AuthContextType extends IAuthUser, AuthContextActions { }

const AuthContext = React.createContext<AuthContextType>({
  username: 'guest',
  fullname: '',
  mail: null,
  groups: [],
  last: 0,
  timeout: -1,
  avatar: null,
  appdata: null,
  signIn: () => { },
  signOut: () => { }
});

const guest: IAuthUser = {
  username: 'guest',
  fullname: '',
  mail: null,
  groups: [],
  last: 0,
  timeout: -1,
  avatar: null,
  appdata: null
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(AuthReducer, guest);
  React.useEffect(() => {
    const initState = async () => {
      try {
        const user = guest; // await getUserData? // TODO:
        if (user !== null) {
          dispatch({ type: "auth.user.signin", payload: user });
        } else {
          dispatch({ type: "auth.user.signout" });
        }
      } catch (e) {
        // catch error here
        // Maybe sign_out user!
      }
    };
    initState();
  }, []);

  const authActions: AuthContextActions = React.useMemo(
    () => ({
      signIn: async (payload: AuthPayload) => {
        dispatch({ type: "auth.user.signin", payload });
        //await setToken(token);
      },
      signOut: async () => {
        //await removeToken(); // TODO: use Vars
        dispatch({ type: "auth.user.signout" });
      },
    }),
    []
  );
  return (
    <AuthContext.Provider value={{ ...state, ...authActions }}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthReducer = (prevState: IAuthUser, action: AuthAction): IAuthUser => {
  switch (action.type) {
    case "auth.user.signin":
      return {
        ...prevState,
        ...action.payload,
        last: Date.now()
      };
    case "auth.user.signout":
      return {
        ...prevState,
        ...guest
      };
  }
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be inside an AuthProvider with a value");
  }
  /*
    you can add more drived state here
    const isLoggedIn  = context.status ==== 'signIn'
    return ({ ...context, isloggedIn})
  */
  return context;
};

export const AuthRequired = ({ children }: { children: JSX.Element }) => {
  //  const current: IAuthUserState | null = useSelector((state: any) => (state && state.authUser ? state.authUser : null))
    const user = useAuth();
    let location = useLocation();
  
    if (!user || !user.username || (user.username === guest.username)) {
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      console.log(`Login required to access ${location.pathname}`);
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  
    return children;
  }
  